// useDebugLogger.ts
import { useState, useEffect } from 'react';
import { DEBUG_MODE } from '@env'; // Assuming you have this in your env variables

export const useDebugLogger = (localDebugOverride?: boolean) => {
  // Use env variable by default, can be overridden locally
  const [isDebugMode, setIsDebugMode] = useState(
    localDebugOverride !== undefined ? localDebugOverride : DEBUG_MODE === true
  );

  // Allow toggling debug mode during development
  const toggleDebugMode = () => {
    setIsDebugMode(prev => !prev);
  };

  // Console method wrappers that only log in debug mode
  const log = (...args: any[]) => {
    if (isDebugMode) {
      console.log('[DEBUG]', ...args);
    }
  };

  const error = (...args: any[]) => {
    if (isDebugMode) {
      console.error('[DEBUG ERROR]', ...args);
    }
  };

  const warn = (...args: any[]) => {
    if (isDebugMode) {
      console.warn('[DEBUG WARNING]', ...args);
    }
  };

  const info = (...args: any[]) => {
    if (isDebugMode) {
      console.info('[DEBUG INFO]', ...args);
    }
  };

  // Group related logs
  const group = (label: string) => {
    if (isDebugMode) {
      console.group(`[DEBUG GROUP] ${label}`);
    }
  };

  const groupEnd = () => {
    if (isDebugMode) {
      console.groupEnd();
    }
  };

  // Log with timestamps
  const timeLog = (...args: any[]) => {
    if (isDebugMode) {
      const timestamp = new Date().toISOString();
      console.log(`[DEBUG ${timestamp}]`, ...args);
    }
  };

  return {
    log,
    error,
    warn,
    info,
    group,
    groupEnd,
    timeLog,
    isDebugMode,
    toggleDebugMode
  };
};

// Non-hook version for use outside of React components
export const createDebugLogger = (isDebugMode: boolean = DEBUG_MODE === true) => {
  return {
    log: (...args: any[]) => isDebugMode && console.log('[DEBUG]', ...args),
    error: (...args: any[]) => isDebugMode && console.error('[DEBUG ERROR]', ...args),
    warn: (...args: any[]) => isDebugMode && console.warn('[DEBUG WARNING]', ...args),
    info: (...args: any[]) => isDebugMode && console.info('[DEBUG INFO]', ...args),
    group: (label: string) => isDebugMode && console.group(`[DEBUG GROUP] ${label}`),
    groupEnd: () => isDebugMode && console.groupEnd(),
    timeLog: (...args: any[]) => {
      if (isDebugMode) {
        const timestamp = new Date().toISOString();
        console.log(`[DEBUG ${timestamp}]`, ...args);
      }
    }
  };
};